"use client";

import { Instagram, Linkedin, Twitter, Music2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="flex flex-col gap-3">
        <li className="h-5 w-24 bg-foreground/10 animate-pulse rounded-md" />
        <li className="h-5 w-20 bg-foreground/10 animate-pulse rounded-md" />
        <li className="h-5 w-28 bg-foreground/10 animate-pulse rounded-md" />
        <li className="h-5 w-16 bg-foreground/10 animate-pulse rounded-md" />
      </ul>;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-foreground/80 hover:text-primary text-[0.9375rem] transition-colors hover:underline underline-offset-4 font-default">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
const companyLinks = [{
  label: "Our Story",
  to: "/OurStoryPage"
}, {
  label: "Preparedness Hub",
  to: "/PreparednessHubPage"
}, {
  label: "Family & Travel",
  to: "/FamilyTravelPage"
}, {
  label: "Shop",
  to: "/ShopPage"
}];
const socials = [{
  name: "Instagram",
  icon: Instagram,
  href: "#"
}, {
  name: "Twitter",
  icon: Twitter,
  href: "#"
}, {
  name: "TikTok",
  icon: Music2,
  href: "#"
}, {
  name: "LinkedIn",
  icon: Linkedin,
  href: "#"
}];
const legalLinks = [{
  label: "Privacy",
  to: "#"
}, {
  label: "Terms",
  to: "#"
}, {
  label: "Security",
  to: "#"
}];
export default function Footer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="7483" id="site-footer" className="bg-slate-900 text-slate-50 py-20 md:py-24">
      <div ref={ref} className={`max-w-[1240px] mx-auto px-5 md:px-16 transition-all duration-[520ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          {}
          <div className="md:col-span-5">
            <Link to="/" className="inline-block">
              <WvcLogo className="h-8 w-auto text-white" />
            </Link>
            <p className="mt-6 text-slate-300 text-base leading-relaxed max-w-sm font-default">
              We're not afraid of the world. We're simply prepared for it.
            </p>
            <div className="mt-8 flex items-center gap-3">
              {socials.map((social, i) => {
              const Icon = social.icon;
              return <Link key={social.name} data-index={i} to={social.href} aria-label={social.name} className="group inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-700 text-slate-300 transition-all duration-220 hover:bg-orange-500 hover:border-orange-500 hover:text-white">
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                  </Link>;
            })}
            </div>
          </div>

          {}
          <div className="md:col-span-2">
            <h4 className="font-default text-[0.8125rem] uppercase font-semibold tracking-[0.08em] text-slate-500 mb-5">
              Resources
            </h4>
            <MenuProvider menu_id="52">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="md:col-span-2">
            <h4 className="font-default text-[0.8125rem] uppercase font-semibold tracking-[0.08em] text-slate-500 mb-5">
              Company
            </h4>
            <ul className="flex flex-col gap-3">
              {companyLinks.map((item, i) => <li key={item.label} data-index={i}>
                  <Link to={item.to} className="text-slate-300 hover:text-primary text-[0.9375rem] transition-colors hover:underline underline-offset-4 font-default">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="md:col-span-3">
            <h4 className="font-default text-[0.8125rem] uppercase font-semibold tracking-[0.08em] text-slate-500 mb-5">
              Get the mist
            </h4>
            <div className="flex flex-col gap-3 mb-5">
              <Link to="/ShopPage" aria-label="Shop B.Y.E. Hygiene Mist" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-slate-700 hover:border-orange-500 transition-colors">
                <div className="flex flex-col leading-none">
                  <span className="text-[0.625rem] text-slate-400 font-default">Shop now at</span>
                  <span className="text-sm text-white font-default font-semibold">B.Y.E. Store</span>
                </div>
              </Link>
            </div>
            <p className="text-[0.8125rem] text-slate-500 font-default">
              Shipping across North America
            </p>
          </div>
        </div>

        {}
        <p className="mt-16 text-center text-[0.9375rem] text-slate-400 font-default italic">
          <span className="font-serif">Before You Enter™</span> — Preparedness for every moment.
        </p>

        {}
        <div className="mt-8 h-px w-full bg-slate-800" />

        {}
        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[0.8125rem] text-slate-500 font-default">
            B.Y.E. Hygiene Mist™ © 2025
          </p>
          <ul className="flex items-center gap-3 text-[0.8125rem] text-slate-500 font-default">
            {legalLinks.map((item, i) => <li key={item.label} data-index={i} className="flex items-center gap-3">
                <Link to={item.to} className="hover:text-white transition-colors">
                  {item.label}
                </Link>
                {i < legalLinks.length - 1 && <span aria-hidden="true">·</span>}
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}